// Detection-window helpers pulled out of app.js. These are the pure-ish
// data functions that turn a metric's `.details` into the highlighted
// playback windows the explorer/chart UI annunciates.
//
// detectionWindowsForHealthKey takes a `context` bag with the three
// helpers that still touch live app.js globals
// (findingCylinderReference, readableProbeLabel, explorerEgtAnnunciationLabel).
// app.js keeps a thin wrapper that builds that bag on each call.
//
// The orchestrators that read selectedHealthKey / healthSummary /
// cached state (selectedDetectionWindows, playbackDetectionWindows) stay
// in app.js since they own those globals.

import { formatCompactElapsedTime } from "../core/format.js";
import { cylinderNumberFromKey } from "../core/schema.js";
import { valveBandLabel } from "../analysis/valve.js";
import { coolingRateParameters } from "../analysis/detectors/cooling-rate.js";
import { magWindowLabel } from "./healthy-metric-evidence.js";

export function pointInDetectionWindow(point, window) {
  if (!point || !window || !Number.isFinite(point.elapsedSeconds)) return false;
  return point.elapsedSeconds >= window.startTime && point.elapsedSeconds <= window.endTime;
}

export function detectionWindow(startTime, endTime, label, key, options: Record<string, any> = {}) {
  if (!Number.isFinite(startTime)) return null;
  const safeEnd = Number.isFinite(endTime) ? endTime : startTime;
  return {
    startTime: Math.min(startTime, safeEnd),
    endTime: Math.max(startTime, safeEnd),
    label,
    key,
    ...options,
  };
}

export function pointDetectionWindow(startTime, label, key, padSeconds = 30, options: Record<string, any> = {}) {
  if (!Number.isFinite(startTime)) return null;
  return detectionWindow(startTime - padSeconds, startTime + padSeconds, label, key, options);
}

function sensorConditionDisplayTime(window, edge) {
  const reviewKey = edge === "start" ? "reviewStartTime" : "reviewEndTime";
  const baseKey = edge === "start" ? "startTime" : "endTime";
  const base = window?.[baseKey];
  if (Number.isFinite(window?.[reviewKey])) return window[reviewKey];
  if (window?.kind === "coolingRateStep" && !Number.isFinite(window?.eventStartTime) && !Number.isFinite(window?.eventEndTime)) {
    const context = edge === "start"
      ? coolingRateParameters.reviewContextBeforeSeconds
      : coolingRateParameters.reviewContextAfterSeconds;
    return Number.isFinite(base) ? base + (edge === "start" ? -context : context) : base;
  }
  return base;
}

export function magCandidateWindow(metric) {
  const candidate = metric?.details?.top;
  if (!candidate || !Number.isFinite(candidate.startTime)) return null;
  const endTime = Number.isFinite(candidate.endTime) ? candidate.endTime : candidate.startTime;
  return {
    candidate,
    startTime: Math.min(candidate.startTime, endTime),
    endTime: Math.max(candidate.startTime, endTime),
    label: magWindowLabel(candidate),
  };
}

function sensorJumpWindows(summary, channelLabel) {
  return (summary?.jumpEvents ?? [])
    .filter((event) => Number.isFinite(event?.time))
    .slice()
    .sort((a, b) => (b.unexplainedDelta ?? 0) - (a.unexplainedDelta ?? 0))
    .slice(0, 3)
    .map((event) => pointDetectionWindow(event.time, `${channelLabel} abrupt jump ${formatCompactElapsedTime(event.time)}`, "sensors", 12, {
      kind: "condition",
      issueKind: "jumps",
      reason: "abrupt data jump",
      channelKey: summary.key,
      channelLabel,
      shortLabel: `${channelLabel} abrupt jump`,
      value: Number.isFinite(event.delta) ? event.delta : null,
      valueSuffix: "",
      valueDigits: 0,
      evidenceLabel: Number.isFinite(event.delta)
        ? `${channelLabel} ${event.delta > 0 ? "+" : ""}${Math.round(event.delta)} jump`
        : `${channelLabel} jump`,
    }))
    .filter(Boolean);
}

export function detectionWindowMatchesCylinder(window, cylinderNumber) {
  if (!Number.isFinite(cylinderNumber)) return true;
  if (!window) return false;
  if (Array.isArray(window.sourceCylinders) && window.sourceCylinders.length) return window.sourceCylinders.includes(cylinderNumber);
  if (Number.isFinite(window.sourceCylinder)) return window.sourceCylinder === cylinderNumber;
  const channelCylinder = cylinderNumberFromKey(window.channelKey);
  return !Number.isFinite(channelCylinder) || channelCylinder === cylinderNumber;
}

export function divergenceEventsAboveVisibilityThreshold(metric) {
  const events = metric?.details?.events?.length ? metric.details.events : [metric?.details?.top].filter(Boolean);
  const topSuspicion = metric?.details?.top?.suspicion ?? 0;
  if (!events.length || !Number.isFinite(topSuspicion)) return [];
  return events.filter((event) => Number.isFinite(event?.startTime) && event.suspicion >= Math.max(10, topSuspicion * 0.22));
}

export function visibleDivergenceCylinders(metric) {
  const eventCylinders = divergenceEventsAboveVisibilityThreshold(metric)
    .flatMap((event) => Array.isArray(event?.cylinders) ? event.cylinders : [])
    .filter(Number.isFinite);
  if (eventCylinders.length) return [...new Set(eventCylinders)];
  return (metric?.details?.top?.cylinders ?? []).filter(Number.isFinite);
}

function valveCandidateMergeGapSeconds(candidate) {
  const cycleSeconds = candidate?.frequency?.cycleSeconds;
  return Math.max(45, Math.min(180, Number.isFinite(cycleSeconds) ? cycleSeconds * 0.75 : 60));
}

function valveRhythmCandidateWindows(metric) {
  const candidates = (Array.isArray(metric?.details?.candidates) ? metric.details.candidates : [])
    .filter((candidate) => (
      candidate?.suspicion > 0
      && Number.isFinite(candidate.startTime)
      && Number.isFinite(candidate.endTime)
    ));
  if (candidates.length) return candidates;
  const best = metric?.details?.top?.best;
  return best?.suspicion > 0 && Number.isFinite(best.startTime) && Number.isFinite(best.endTime)
    ? [best]
    : [];
}

function valveRhythmWindowClusters(metric) {
  const sorted = valveRhythmCandidateWindows(metric)
    .map((candidate) => {
      const startTime = Math.min(candidate.startTime, candidate.endTime);
      const endTime = Math.max(candidate.startTime, candidate.endTime);
      return {
        startTime,
        endTime,
        cylinder: candidate.cylinder ?? cylinderNumberFromKey(candidate.key),
        candidate,
        mergeGapSeconds: valveCandidateMergeGapSeconds(candidate),
      };
    })
    .filter((item) => Number.isFinite(item.startTime) && Number.isFinite(item.endTime))
    .sort((a, b) => (a.cylinder ?? Infinity) - (b.cylinder ?? Infinity) || a.startTime - b.startTime || a.endTime - b.endTime);

  const clusters = [];
  sorted.forEach((item) => {
    const current = clusters.at(-1);
    if (current && current.cylinder === item.cylinder && item.startTime <= current.endTime + Math.max(current.mergeGapSeconds, item.mergeGapSeconds)) {
      current.startTime = Math.min(current.startTime, item.startTime);
      current.endTime = Math.max(current.endTime, item.endTime);
      current.mergeGapSeconds = Math.max(current.mergeGapSeconds, item.mergeGapSeconds);
      current.candidateCount += 1;
      if ((item.candidate.suspicion ?? 0) > (current.best.suspicion ?? 0)) current.best = item.candidate;
      return;
    }
    clusters.push({
      startTime: item.startTime,
      endTime: item.endTime,
      cylinder: item.cylinder,
      best: item.candidate,
      candidateCount: 1,
      mergeGapSeconds: item.mergeGapSeconds,
    });
  });
  return clusters.sort((a, b) => a.startTime - b.startTime || (b.best?.suspicion ?? 0) - (a.best?.suspicion ?? 0));
}

function valveRhythmDetectionWindows(metric, context) {
  const { findingCylinderReference } = context;
  return valveRhythmWindowClusters(metric).map((cluster) => {
    const best = cluster.best ?? {};
    const cylinder = Number.isFinite(cluster.cylinder) ? cluster.cylinder : metric?.details?.top?.cylinder;
    const cylinderLabel = Number.isFinite(cylinder) ? ` ${findingCylinderReference(cylinder).label}` : "";
    const prefix = valveBandLabel(best.frequency?.band);
    const channelLabel = Number.isFinite(cylinder) ? `EGT ${cylinder}` : "EGT";
    return detectionWindow(
      cluster.startTime,
      cluster.endTime,
      `${prefix}${cylinderLabel} ${formatCompactElapsedTime(cluster.startTime)}-${formatCompactElapsedTime(cluster.endTime)}`,
      "valve",
      {
        kind: "egt-rhythm",
        channelKey: Number.isFinite(cylinder) ? `egt${cylinder}` : best.key ?? null,
        channelLabel,
        sourceCylinder: cylinder,
        shortLabel: `${channelLabel} rhythm`,
        value: Number.isFinite(best.amplitude) ? best.amplitude : null,
        valueSuffix: " F",
        valueDigits: 0,
        evidenceLabel: Number.isFinite(best.amplitude) ? `${channelLabel} ${Math.round(best.amplitude)} F rhythm` : `${channelLabel} rhythm`,
        amplitude: best.amplitude,
        ratio: best.ratio,
        frequency: best.frequency,
        suspicion: best.suspicion,
        candidateCount: cluster.candidateCount,
        bestStartTime: best.startTime,
        bestEndTime: best.endTime,
      },
    );
  }).filter(Boolean);
}

export function detectionWindowsForHealthKey(key, metric, context: Record<string, any> = {}) {
  const {
    findingCylinderReference = () => ({ label: "this cylinder", location: "" }),
    readableProbeLabel = () => "",
    explorerEgtAnnunciationLabel = () => "",
  } = context;
  if (!metric) return [];
  const scoreIsFlagged = Number.isFinite(metric.score) && metric.score < 88;

  if (key === "instrumentationSuspicion") {
    // Sensors retains qualified reference evidence as context across sensitivity modes.
    const rawFlagged = Number.isFinite(metric.rawScore) && metric.rawScore < 88;
    if (!Number.isFinite(metric.score) || (!scoreIsFlagged && !rawFlagged)) return [];
    return (metric.details?.windows ?? []).filter(window => window.coherent
      && Number.isFinite(window.startTime) && Number.isFinite(window.endTime) && window.endTime > window.startTime)
      .slice(0, 6).map(window => detectionWindow(window.startTime, window.endTime,
        `Probe reference ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`, key));
  }
  if (key === "mag") {
    if (Array.isArray(metric.details?.events) && metric.details.events.length) {
      return metric.details.events.map((event) => detectionWindow(event.startTime, event.endTime,
        `Mag check ${event.checkLabel ? `${event.checkLabel.toLowerCase()} ` : ""}${magWindowLabel(event)}`, "mag", {
          responseStatus: event.responseStatus, confidence: event.confidence, score: event.score,
          sequenceIndex: event.sequenceIndex, checkIndex: event.checkIndex,
        })).filter(Boolean);
    }
    const window = magCandidateWindow(metric);
    if (!window) return [];
    return [{ ...window, key: "mag", label: `Mag check ${window.label}` }];
  }
  if (key === "carbIce") {
    if (!scoreIsFlagged) return [];
    const top = metric.details?.top;
    if (!top || !Number.isFinite(top.startTime)) return [];
    return [detectionWindow(top.startTime, top.endTime, `Carb ice cue ${formatCompactElapsedTime(top.startTime)}-${formatCompactElapsedTime(top.endTime)}`, "carbIce")].filter(Boolean);
  }
  if (key === "turbo") {
    const eventWindows = metric.details?.eventWindows ?? [];
    if (eventWindows.length) return eventWindows.filter((window) => Number.isFinite(window.startTime)).slice(0, 6).map((window) => detectionWindow(window.startTime, window.endTime ?? window.startTime + 30, window.label ?? "Turbo review", "turbo")).filter(Boolean);
    if (metric.details?.status === "pass") return [];
    const top = metric.details?.top;
    if (!metric.details?.available || !Number.isFinite(top?.elapsedSeconds)) return [];
    return [pointDetectionWindow(top.elapsedSeconds, metric.details?.status === "needs-profile-limits" ? "Turbo profile limits needed" : "Turbo context", "turbo")].filter(Boolean);
  }
  if (key === "oilSystem") {
    if (!scoreIsFlagged) return [];
    return (metric.details?.windows ?? []).slice().sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0)).slice(0, 6).map((window) => detectionWindow(window.startTime, window.endTime, `${window.label ?? "Oil system"} ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`, "oilSystem")).filter(Boolean);
  }
  if (key === "coolantMargin") {
    if (!scoreIsFlagged) return [];
    return (metric.details?.windows ?? [])
      .slice()
      .sort((a, b) => (b.severity ?? 0) - (a.severity ?? 0) || (b.duration ?? 0) - (a.duration ?? 0))
      .slice(0, 6)
      .map((window) => detectionWindow(window.startTime, window.endTime, `${window.label ?? "Coolant margin"} ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`, "coolantMargin", {
        channelKey: "coolantTemp",
        channelLabel: window.channelLabel ?? "Coolant Temp",
        value: Number.isFinite(window.maxValue) ? window.maxValue : null,
        valueSuffix: " F",
        valueDigits: 0,
        evidenceLabel: Number.isFinite(window.maxValue) ? `Coolant max ${Math.round(window.maxValue)} F` : "Coolant margin",
      }))
      .filter(Boolean);
  }
  if (key === "takeoffEnrichment") {
    if (!scoreIsFlagged) return [];
    return (metric.details?.windows ?? []).filter((window) => Number.isFinite(window.startTime) && Number.isFinite(window.endTime)).slice(0, 4).map((window) => detectionWindow(window.startTime, window.endTime, `${window.label ?? "Full-power window"} ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`, "takeoffEnrichment")).filter(Boolean);
  }
  if (key === "valve") {
    return valveRhythmDetectionWindows(metric, { findingCylinderReference });
  }
  if (key === "combustion") {
    const top = metric.details?.top;
    if (!top || !scoreIsFlagged || !Number.isFinite(top.startTime)) return [];
    const cylinderLabel = Number.isFinite(top.cylinder) ? ` ${findingCylinderReference(top.cylinder).label}` : "";
    return [detectionWindow(top.startTime, top.endTime, `${top.label ?? "CHT/EGT Pairing"}${cylinderLabel} ${formatCompactElapsedTime(top.startTime)}-${formatCompactElapsedTime(top.endTime)}`, "combustion", {
      channelKey: Number.isFinite(top.cylinder) ? `egt${top.cylinder}` : null,
      sourceCylinder: top.cylinder,
    })].filter(Boolean);
  }
  if (key === "divergence" || key === "descentEgtDropout") {
    const events = metric.details?.events?.length ? metric.details.events : [metric.details?.top].filter(Boolean);
    const topSuspicion = metric.details?.top?.suspicion ?? 0;
    const hasDivergenceCandidate = key === "divergence" && divergenceEventsAboveVisibilityThreshold(metric).length > 0;
    const hasDropoutCandidate = key === "descentEgtDropout" && Number.isFinite(metric.details?.top?.dropoutF) && topSuspicion >= 10;
    if ((!scoreIsFlagged && !hasDivergenceCandidate && !hasDropoutCandidate) || !events.length || !Number.isFinite(topSuspicion)) return [];
    return events.filter((event) => Number.isFinite(event.startTime) && event.suspicion >= Math.max(key === "descentEgtDropout" ? 12 : 10, topSuspicion * 0.22)).slice(0, 8).sort((a, b) => a.startTime - b.startTime).map((event, index, visibleEvents) => {
      const startTime = event.clusterStartTime ?? event.startTime;
      const endTime = event.clusterEndTime ?? event.endTime;
      const prefix = key === "descentEgtDropout" ? (visibleEvents.length > 1 ? `EGT dropout ${index + 1}` : "EGT dropout") : (visibleEvents.length > 1 ? `Divergence ${index + 1}` : "Divergence");
      const cylinder = key === "descentEgtDropout" && Number.isFinite(event.cylinder) ? ` ${findingCylinderReference(event.cylinder).label}` : "";
      return detectionWindow(startTime, endTime, `${prefix}${cylinder} ${formatCompactElapsedTime(event.startTime)}-${formatCompactElapsedTime(event.endTime)}`, key, {
        channelKey: Number.isFinite(event.cylinder) ? `egt${event.cylinder}` : null,
        sourceCylinder: event.cylinder,
        sourceCylinders: event.cylinders,
      });
    }).filter(Boolean);
  }
  if (key === "inductionLeak") {
    if (!scoreIsFlagged) return [];
    const events = (metric.details?.separatedEvents?.length ? metric.details.separatedEvents : [metric.details?.top].filter(Boolean))
      .filter((event) => event && Number.isFinite(event.startTime) && Number.isFinite(event.endTime))
      .slice()
      .sort((a, b) => a.startTime - b.startTime);
    if (!events.length) return [];
    return events.map((event, index) => {
      const cylinderLabel = Number.isFinite(event.cylinder) ? ` ${findingCylinderReference(event.cylinder).label}` : "";
      const channelLabel = event.key ? (readableProbeLabel(event.key) || event.key.toUpperCase()) : "EGT";
      const rise = Number.isFinite(event.cylinderEgtChange) ? event.cylinderEgtChange : event.egtChange;
      const cylinderRise = Number.isFinite(rise) ? `, ${rise > 0 ? "+" : ""}${Math.round(rise)} F EGT change` : "";
      const prefix = events.length > 1 ? `Induction leak cue ${index + 1}` : "Induction leak cue";
      return detectionWindow(event.startTime, event.endTime, `${prefix}${cylinderLabel} ${formatCompactElapsedTime(event.startTime)}-${formatCompactElapsedTime(event.endTime)}`, "inductionLeak", {
        channelKey: event.key,
        channelLabel,
        sourceCylinder: event.cylinder,
        value: rise,
        valueSuffix: " F",
        valueDigits: 0,
        evidenceLabel: Number.isFinite(event.cylinder) ? `${channelLabel}${cylinderRise}` : `EGT${cylinderRise}`,
      });
    }).filter(Boolean);
  }
  if (key === "cooling") {
    const window = metric.details?.top?.evidenceWindow;
    if (!window || !Number.isFinite(window.startTime)) return [];
    const cylinder = metric.details?.top?.cylinder;
    const channelKey = Number.isFinite(cylinder) ? `cht${cylinder}` : null;
    const channelLabel = channelKey ? (readableProbeLabel(channelKey) || `CHT${cylinder}`) : "CHT";
    const value = Number.isFinite(window.endCht) ? window.endCht : metric.details?.top?.p95Cht;
    const delta = Number.isFinite(window.averageChtDelta) ? `, +${Math.round(window.averageChtDelta)} F vs peers` : "";
    return [detectionWindow(window.startTime, window.endTime, `Cooling${Number.isFinite(cylinder) ? ` ${findingCylinderReference(cylinder).label}` : ""} ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`, "cooling", {
      channelKey,
      channelLabel,
      sourceCylinder: cylinder,
      value,
      valueSuffix: " F",
      valueDigits: 0,
      evidenceLabel: Number.isFinite(value) ? `${channelLabel} ${Math.round(value)} F${delta}` : `${channelLabel} cooling`,
    })].filter(Boolean);
  }
  if (key === "coolingRate") {
    if (!scoreIsFlagged) return [];
    const events = metric.details?.events?.length ? metric.details.events : [metric.details?.top].filter(Boolean);
    return events.filter((event) => Number.isFinite(event.startTime) && Number.isFinite(event.endTime)).slice(0, 6).sort((a, b) => a.startTime - b.startTime).map((event) => {
      const channelLabel = readableProbeLabel(event.key) || event.label || "CHT";
      const windowStart = Number.isFinite(event.reviewStartTime) ? event.reviewStartTime : event.startTime;
      const windowEnd = Number.isFinite(event.reviewEndTime) ? event.reviewEndTime : event.endTime;
      return detectionWindow(windowStart, windowEnd, `${channelLabel} cooling ${Math.round(event.rateFpm ?? 0)} F/min`, "coolingRate", {
        channelKey: event.key,
        channelLabel,
        sourceCylinder: event.cylinder,
        eventStartTime: event.startTime,
        eventEndTime: event.endTime,
        value: event.rateFpm,
        valueSuffix: " F/min",
        valueDigits: 0,
        evidenceLabel: Number.isFinite(event.rateFpm) ? `${channelLabel} ${Math.round(event.rateFpm)} F/min` : `${channelLabel} cooling`,
      });
    }).filter(Boolean);
  }
  if (key === "charging") {
    const charging = metric.details;
    if (!charging || !Number.isFinite(charging.startTime)) return [];
    return [detectionWindow(charging.startTime - 15, charging.endTime ?? charging.startTime + 180, `Start charge ${charging.status ?? "not confirmed"} ${formatCompactElapsedTime(charging.startTime)}`, "charging")].filter(Boolean);
  }
  if (key === "engineDelta") {
    if (!scoreIsFlagged) return [];
    const window = metric.details?.primary?.window;
    if (!window || !Number.isFinite(window.startTime)) return [];
    return [detectionWindow(window.startTime, window.endTime, `${metric.details?.primary?.label ?? "Engine delta"} ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`, "engineDelta", { scope: "comparison" })].filter(Boolean);
  }
  if (key === "egt") {
    const separation = !metric.details?.hot?.isReview && !metric.details?.sustainedLowEgt
      ? metric.details?.sustainedEgtSeparation : null;
    if (separation && Number.isFinite(separation.startTime) && Number.isFinite(separation.endTime)) {
      return [detectionWindow(separation.startTime, separation.endTime, "Sustained EGT separation", "egt", {
        kind: "sustained-separation", channelKey: separation.key, sourceCylinder: separation.cylinder,
        shortLabel: "EGT separation", stats: { p90DeviationF: separation.p90DeviationF, maxPeerSpreadF: separation.maxPeerSpreadF },
      })];
    }
    const sustainedLow = metric.details?.sustainedLowEgt;
    if (sustainedLow && Number.isFinite(sustainedLow.startTime)) {
      const channelLabel = readableProbeLabel(sustainedLow.key) || sustainedLow.label || (Number.isFinite(sustainedLow.cylinder) ? `EGT${sustainedLow.cylinder}` : "EGT");
      const cylinder = Number.isFinite(sustainedLow.cylinder) ? ` ${findingCylinderReference(sustainedLow.cylinder).label}` : "";
      const deviation = Number.isFinite(sustainedLow.p90DeviationF) ? `, ${Math.round(sustainedLow.p90DeviationF)} F below peers` : "";
      const windowStart = Number.isFinite(sustainedLow.reviewStartTime) ? sustainedLow.reviewStartTime : sustainedLow.startTime;
      const windowEnd = Number.isFinite(sustainedLow.reviewEndTime) ? sustainedLow.reviewEndTime : sustainedLow.endTime;
      return [detectionWindow(
        windowStart,
        windowEnd,
        `Sustained EGT drop${cylinder} ${formatCompactElapsedTime(windowStart)}-${formatCompactElapsedTime(windowEnd)}`,
        "egt",
        {
          kind: "sustained-low",
          channelKey: sustainedLow.key,
          channelLabel,
          shortLabel: `${channelLabel} low`,
          sourceCylinder: sustainedLow.cylinder,
          eventStartTime: sustainedLow.startTime,
          eventEndTime: sustainedLow.endTime,
          value: sustainedLow.p90DeviationF,
          valueSuffix: " F",
          valueDigits: 0,
          evidenceLabel: `${channelLabel}${deviation}`,
          strength: sustainedLow.strength,
          sampleShare: sustainedLow.sampleShare,
          stepDown: sustainedLow.stepDown,
        },
      )].filter(Boolean);
    }
    const hot = metric.details?.hot;
    if (hot?.isReview && Number.isFinite(hot.maxTime)) {
      const shortLabel = explorerEgtAnnunciationLabel(metric) || "EGT HIGH";
      const channelLabel = readableProbeLabel(hot.key) || readableProbeLabel(hot.label) || hot.label || "EGT";
      return [detectionWindow(hot.maxTime - 30, hot.maxTime + 30, `${channelLabel} high ${formatCompactElapsedTime(hot.maxTime)}`, "egt", { kind: "absolute-high", channelKey: hot.key, channelLabel, shortLabel, value: hot.max, sourceCylinder: hot.cylinder })].filter(Boolean);
    }
    const peak = metric.details?.peak;
    if (!peak || !scoreIsFlagged || !Number.isFinite(peak.elapsedSeconds)) return [];
    const cylinder = metric.details?.dominant?.cylinder ?? peak.high?.cylinder ?? peak.low?.cylinder;
    return [pointDetectionWindow(peak.elapsedSeconds, `EGT spread ${formatCompactElapsedTime(peak.elapsedSeconds)}`, "egt", 30, {
      channelKey: Number.isFinite(cylinder) ? `egt${cylinder}` : null,
      sourceCylinder: cylinder,
    })].filter(Boolean);
  }
  if (key === "cht") {
    const peak = metric.details?.peak;
    const hottest = peak ? { key: peak.key, cylinder: peak.cylinder, max: peak.value, maxTime: peak.elapsedSeconds } : metric.details?.hottest;
    if (!hottest || !scoreIsFlagged || !Number.isFinite(hottest.maxTime)) return [];
    const channelLabel = Number.isFinite(hottest.cylinder) ? (readableProbeLabel(`cht${hottest.cylinder}`) || `CHT${hottest.cylinder}`) : "CHT";
    const windows = [pointDetectionWindow(hottest.maxTime, `${channelLabel} peak ${formatCompactElapsedTime(hottest.maxTime)}`, "cht", 30, {
      kind: "absolute-high",
      channelKey: hottest.key,
      channelLabel,
      sourceCylinder: hottest.cylinder,
      value: hottest.max,
      valueSuffix: " F",
      valueDigits: 0,
      evidenceLabel: Number.isFinite(hottest.max) ? `${channelLabel} ${Math.round(hottest.max)} F` : `${channelLabel} peak`,
    })];
    const event = metric.details?.severityFloor;
    if (event) windows.push(detectionWindow(event.startTime, event.endTime,
      `${readableProbeLabel(event.channelKey) || event.channelKey} at or above ${event.thresholdF} F`, "cht", {
        kind: "thermal-exposure", channelKey: event.channelKey, sourceCylinder: event.sourceCylinder,
        value: event.peakF, valueSuffix: " F", valueDigits: 0,
      }));
    return windows.filter(Boolean);
  }
  if (key === "abnormalCombustion") {
    const event = metric.details?.severityFloor;
    if (!scoreIsFlagged || !event) return [];
    return [detectionWindow(event.startTime, event.endTime, "CHT rise with flat or falling EGT", key, {
      channelKey: event.channelKey, sourceCylinder: event.sourceCylinder, kind: "cht-egt-decoupling",
    })].filter(Boolean);
  }
  if (key === "chtRhythm") {
    const best = metric.details?.top?.best;
    const cylinder = metric.details?.top?.cylinder;
    const cylinders = Array.isArray(metric.details?.top?.cylinders)
      ? metric.details.top.cylinders.filter(Number.isFinite)
      : [cylinder].filter(Number.isFinite);
    if (!best || !scoreIsFlagged || !Number.isFinite(best.startTime) || !Number.isFinite(best.endTime)) return [];
    const channelKey = Number.isFinite(cylinder) ? `cht${cylinder}` : best.key;
    const channelLabel = channelKey ? (readableProbeLabel(channelKey) || channelKey.toUpperCase()) : "CHT";
    const amplitude = Number.isFinite(best.amplitude) ? `${Math.round(best.amplitude)} F` : "CHT";
    const timing = metric.details?.timing;
    const startTime = Number.isFinite(timing?.startTime) ? timing.startTime : best.startTime;
    const endTime = Number.isFinite(timing?.endTime) ? timing.endTime : best.endTime;
    return [detectionWindow(startTime, endTime, `${channelLabel} rhythm ${formatCompactElapsedTime(startTime)}-${formatCompactElapsedTime(endTime)}`, "chtRhythm", {
      kind: "cht-rhythm",
      channelKey,
      channelLabel,
      sourceCylinder: cylinder,
      sourceCylinders: cylinders,
      shortLabel: `${channelLabel} rhythm`,
      value: Number.isFinite(best.amplitude) ? best.amplitude : null,
      valueSuffix: " F",
      valueDigits: 0,
      evidenceLabel: `${channelLabel} ${amplitude} rhythm`,
    })].filter(Boolean);
  }
  if (key === "sensors") {
    const summaries = metric.details?.summaries?.length ? metric.details.summaries : [metric.details?.top].filter(Boolean);
    const hasConditionCue = summaries.some((summary) => ((summary?.conditionWindows?.length && summary.suspicion >= 18) || summary?.flatline));
    const hasJumpCue = summaries.some((summary) => (summary?.jumpEvents?.length ?? 0) > 0 || (summary?.impossibleJumpCount ?? 0) > 0);
    if (!scoreIsFlagged && !hasConditionCue && !hasJumpCue) return [];
    return summaries.filter((summary) => summary?.suspicion >= 18 || summary === metric.details?.top || (summary?.jumpEvents?.length ?? 0) > 0 || (summary?.impossibleJumpCount ?? 0) > 0).flatMap((summary) => {
      const channelLabel = summary.label ?? "Probe";
      const conditionWindows = (summary.conditionWindows ?? []).slice(0, 3).map((window) => {
        const windowStart = sensorConditionDisplayTime(window, "start");
        const windowEnd = sensorConditionDisplayTime(window, "end");
        return detectionWindow(windowStart, windowEnd, `${channelLabel} ${window.reason ?? "signal review"} ${formatCompactElapsedTime(windowStart)}-${formatCompactElapsedTime(windowEnd)}`, "sensors", { kind: "condition", issueKind: window.kind, reason: window.reason, channelKey: summary.key, channelLabel, shortLabel: `${channelLabel} signal review`, affectedRatio: window.affectedRatio, duration: window.duration, eventStartTime: window.eventStartTime, eventEndTime: window.eventEndTime, rangeMin: window.rangeMin ?? summary.rangeMin, rangeMax: window.rangeMax ?? summary.rangeMax, worstLowValue: window.worstLowValue ?? summary.worstLowValue, worstLowTime: window.worstLowTime ?? summary.worstLowTime, worstHighValue: window.worstHighValue ?? summary.worstHighValue, worstHighTime: window.worstHighTime ?? summary.worstHighTime, outOfRangeLowCount: window.outOfRangeLowCount ?? summary.outOfRangeLowCount, outOfRangeHighCount: window.outOfRangeHighCount ?? summary.outOfRangeHighCount });
      });
      const flatline = summary.flatline ? [detectionWindow(summary.flatline.startTime, summary.flatline.endTime, `${channelLabel} flatline ${formatCompactElapsedTime(summary.flatline.startTime)}-${formatCompactElapsedTime(summary.flatline.endTime)}`, "sensors", { kind: "condition", issueKind: "flatline", reason: "flatline", channelKey: summary.key, channelLabel, shortLabel: `${channelLabel} flatline`, duration: summary.flatline.duration })] : [];
      const jumpWindows = conditionWindows.some((window) => window?.issueKind === "jumps")
        ? []
        : sensorJumpWindows(summary, channelLabel);
      return [...conditionWindows, ...flatline, ...jumpWindows];
    }).filter(Boolean).sort((a, b) => (b.duration ?? 0) - (a.duration ?? 0)).slice(0, 6);
  }
  if (key === "electrical") {
    if (!scoreIsFlagged) return [];
    if (metric.details?.startSag && Number.isFinite(metric.details.startMinTime)) return [pointDetectionWindow(metric.details.startMinTime, `Start sag ${formatCompactElapsedTime(metric.details.startMinTime)}`, "electrical")].filter(Boolean);
    const window = metric.details?.outOfRangeWindow;
    if (window && Number.isFinite(window.startTime)) return [detectionWindow(window.startTime, window.endTime, `Bus voltage ${formatCompactElapsedTime(window.startTime)}-${formatCompactElapsedTime(window.endTime)}`, "electrical")].filter(Boolean);
  }
  return [];
}
